import { useState, useEffect, Fragment } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getPhoneErrors, getPhoneErrorStatistics } from '@services/api';
import { useDebounce } from '@hooks/useDebounce';
import { usePersistentState } from '@hooks/usePersistentState';
import { formatDateTime, formatTime } from '@utils/format';
import { parseApiError } from '@utils/errorUtils';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import './ErrorsTab.css';

// Debounce for the free-text filter so we don't fire a search on every keystroke.
const SEARCH_DEBOUNCE_MS = 350;

// Formats a Date for a datetime-local input using LOCAL wall-clock time.
// Returns '' for null/invalid dates so clearing the field can't crash the render.
const toDateTimeLocalValue = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

export default function ErrorsTab() {
  // Pagination + filter state persist across tab switches (the tab unmounts when
  // you navigate away) so returning to Errors restores the last view.
  const [page, setPage] = usePersistentState('errors.page', 1);
  const [pageSize, setPageSize] = usePersistentState('errors.pageSize', 25);
  const [twilioAccountSid, setTwilioAccountSid] = useState(null);

  // Get the Twilio Account SID from auth token for console links
  useEffect(() => {
    try {
      const authToken = sessionStorage.getItem('authToken');
      if (authToken) {
        const payload = authToken.split('.')[1];
        if (payload) {
          const decoded = JSON.parse(atob(payload));
          if (decoded.twilio_account_sid) {
            setTwilioAccountSid(decoded.twilio_account_sid);
          }
        }
      }
    } catch (err) {
      console.debug('Could not extract account SID from token:', err);
    }
  }, []);

  // Filter state
  const [filters, setFilters] = usePersistentState('errors.filters', () => ({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
    endDate: new Date(),
    category: '',
    severity: '',
    callSid: '',
  }));

  const [showFilters, setShowFilters] = useState(false);
  const [expandedErrorId, setExpandedErrorId] = useState(null);

  // Debounce the filter object so typing in the Call SID filter doesn't fire a
  // request per keystroke; the debounced value feeds the query key below.
  const debouncedFilters = useDebounce(filters, SEARCH_DEBOUNCE_MS);

  // React Query gives us result caching across tab switches, built-in out-of-order
  // response handling, and (via placeholderData) keeps the current table on screen
  // while the next query loads so it never blanks on a filter change.
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: ['phoneErrors', debouncedFilters, page, pageSize],
    queryFn: () =>
      getPhoneErrors({
        ...debouncedFilters,
        page,
        pageSize,
        includeStatistics: true, // Request statistics in the same call
      }),
    placeholderData: keepPreviousData,
  });

  const errors = data?.errors ?? [];
  const statistics = data?.statistics ?? null;
  const totalPages = data?.totalPages ?? 0;
  const totalCount = data?.totalCount ?? 0;

  // Fetch the full category list for the date range (independent of the category/severity
  // filter, so selecting one category doesn't collapse the dropdown to only that category).
  const { data: availableCategories = [] } = useQuery({
    queryKey: ['phoneErrorCategories', debouncedFilters.startDate, debouncedFilters.endDate],
    queryFn: () =>
      getPhoneErrorStatistics({
        startDate: debouncedFilters.startDate,
        endDate: debouncedFilters.endDate,
      }),
    select: (stats) =>
      (stats?.byCategory || [])
        .map((c) => c.category)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
  });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
      endDate: new Date(),
      category: '',
      severity: '',
      callSid: '',
    });
    setPage(1);
  };

  // Colors reference the shared design tokens in styles/colors.css.
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'var(--color-severity-critical)';
      case 'high':
        return 'var(--color-severity-high)';
      case 'medium':
        return 'var(--color-severity-medium)';
      case 'low':
        return 'var(--color-severity-low)';
      default:
        return 'var(--color-neutral)';
    }
  };

  // Fallback category list used only until the statistics-derived list arrives.
  const fallbackCategories = [
    'Outgoing',
    'Incoming',
    'IVR',
    'Transfer',
    'Voicemail',
    'Recording',
    'Webhook',
  ];
  const categoryOptions =
    availableCategories.length > 0 ? availableCategories : fallbackCategories;

  const severityLevels = ['Low', 'Medium', 'High', 'Critical'];

  // Build a severity → count lookup so we can always render dedicated cards
  // (including High/Critical) even when a level has zero errors or is absent.
  const severityCounts = (statistics?.bySeverity || []).reduce((acc, s) => {
    if (s.severity) acc[s.severity.toLowerCase()] = s.count;
    return acc;
  }, {});

  const getTwilioConsoleUrl = (callSid) => {
    if (!callSid) return null;

    // Validate callSid format to prevent open-redirect / DOM XSS via URL injection.
    // Twilio call SIDs always start with "CA" followed by 32 hex characters.
    if (!/^CA[0-9a-fA-F]{32}$/.test(callSid)) return null;

    const frameUrl = `/console/voice/calls/logs/${callSid}`;
    const base = new URL('https://console.twilio.com/us1/monitor/logs/calls');
    base.searchParams.set('frameUrl', frameUrl);
    base.searchParams.set('x-target-region', 'us1');
    base.searchParams.set('__override_layout__', 'embed');
    base.searchParams.set('bifrost', 'true');

    if (twilioAccountSid) {
      base.searchParams.set('accountSid', twilioAccountSid);
    }
    return base.toString();
  };

  const CallSidLink = ({ sid }) => {
    if (!sid) return null;
    return (
      <a
        href={getTwilioConsoleUrl(sid)}
        target="_blank"
        rel="noopener noreferrer"
        className="call-sid-link"
        title="View in Twilio Console"
        onClick={(e) => e.stopPropagation()}
      >
        {sid}
      </a>
    );
  };

  return (
    <div className="errors-tab">
      {/* Header */}
      <div className="errors-header">
        <h2><span aria-hidden="true">📋</span> System Errors</h2>
        <div className="errors-header-actions">
          {dataUpdatedAt > 0 && (
            <span className="last-updated">
              Updated {formatTime(dataUpdatedAt)}
            </span>
          )}
          <button
            className="refresh-button"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Reload errors with the current filters"
          >
            <span aria-hidden="true">🔄</span> Refresh
          </button>
          <button
            className="filter-toggle-button"
            onClick={() => setShowFilters(!showFilters)}
          >
            <span aria-hidden="true">{showFilters ? '⬆' : '⬇'}</span>{' '}
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(filters.startDate)}
              onChange={(e) =>
                handleFilterChange(
                  'startDate',
                  e.target.value ? new Date(e.target.value) : null
                )
              }
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(filters.endDate)}
              onChange={(e) =>
                handleFilterChange(
                  'endDate',
                  e.target.value ? new Date(e.target.value) : null
                )
              }
            />
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Severity</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="">All Severity Levels</option>
              {severityLevels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Call SID</label>
            <input
              type="text"
              placeholder="Filter by Twilio Call SID"
              value={filters.callSid}
              onChange={(e) => handleFilterChange('callSid', e.target.value)}
            />
          </div>

          <button className="clear-filters-button" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      )}

      {/* Statistics — High/Critical stay visible regardless of the severity filter
          because the backend computes stats without applying the severity filter. */}
      {statistics && (
        <div className="statistics-panel">
          <div className="stat-card">
            <div className="stat-number">{statistics.totalErrors}</div>
            <div className="stat-label">Total (Filter)</div>
          </div>

          {severityLevels.map((level) => {
            const key = level.toLowerCase();
            const emphasized = key === 'high' || key === 'critical';
            return (
              <div
                key={level}
                className={`stat-card${emphasized ? ' stat-card-emphasis' : ''}`}
              >
                <div
                  className="stat-number"
                  style={{ color: getSeverityColor(level) }}
                >
                  {severityCounts[key] || 0}
                </div>
                <div className="stat-label">{level}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter-aware result count */}
      <div className="results-summary">
        Showing <strong>{totalCount}</strong>{' '}
        {totalCount === 1 ? 'error' : 'errors'} for the current filter
      </div>

      {/* Errors Table */}
      <div className="errors-container">
        {isError && (
          <div className="error-message">
            Failed to fetch errors: {parseApiError(error)}
          </div>
        )}

        {isLoading ? (
          <Loading message="Loading errors..." />
        ) : errors.length === 0 ? (
          <EmptyState className="no-errors" message="No errors found for the selected filters" />
        ) : (
          <>
            {isFetching && (
              <div className="results-updating" aria-live="polite">
                Updating…
              </div>
            )}
            <div className="errors-table-wrapper">
              <table className="errors-table">
                <thead>
                  <tr>
                    <th className="col-time">Time</th>
                    <th className="col-severity">Severity</th>
                    <th className="col-category">Category</th>
                    <th className="col-service">Service</th>
                    <th className="col-message">Message</th>
                    <th className="col-callsid">Call SID</th>
                    <th className="col-contact">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((err) => {
                    const isExpanded = expandedErrorId === err.phoneErrorId;
                    return (
                      <Fragment key={err.phoneErrorId}>
                        <tr
                          className={`error-row${isExpanded ? ' expanded' : ''}`}
                          onClick={() =>
                            setExpandedErrorId(isExpanded ? null : err.phoneErrorId)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setExpandedErrorId(
                                isExpanded ? null : err.phoneErrorId
                              );
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-expanded={isExpanded}
                        >
                          <td className="col-time">
                            {formatDateTime(err.createdAt)}
                          </td>
                          <td className="col-severity">
                            <span
                              className="severity-badge"
                              style={{
                                backgroundColor: getSeverityColor(err.severity),
                              }}
                            >
                              {err.severity}
                            </span>
                          </td>
                          <td className="col-category">
                            <span className="error-category">{err.category}</span>
                          </td>
                          <td className="col-service">{err.service}</td>
                          <td className="col-message" title={err.errorMessage}>
                            {err.errorMessage?.substring(0, 120)}
                            {err.errorMessage?.length > 120 ? '…' : ''}
                          </td>
                          <td className="col-callsid">
                            <CallSidLink sid={err.callSid} />
                          </td>
                          <td className="col-contact">{err.contactId || '—'}</td>
                        </tr>

                        {isExpanded && (
                          <tr className="error-detail-row">
                            <td colSpan={7}>
                              <div className="error-details">
                                <div className="detail-section">
                                  <h4>Error Message</h4>
                                  <p>{err.errorMessage}</p>
                                </div>

                                {(err.callSid || err.parentCallSid) && (
                                  <div className="detail-section">
                                    <h4>Call Information</h4>
                                    {err.callSid && (
                                      <p>
                                        <strong>Call SID:</strong>{' '}
                                        <CallSidLink sid={err.callSid} />
                                      </p>
                                    )}
                                    {err.parentCallSid && (
                                      <p>
                                        <strong>Parent Call SID:</strong>{' '}
                                        <CallSidLink sid={err.parentCallSid} />
                                      </p>
                                    )}
                                  </div>
                                )}

                                {err.contactId && (
                                  <div className="detail-section">
                                    <h4>Contact Information</h4>
                                    <p>
                                      <strong>Contact ID:</strong> {err.contactId}
                                    </p>
                                  </div>
                                )}

                                {err.additionalContext && (
                                  <div className="detail-section">
                                    <h4>Additional Context</h4>
                                    <pre className="context-json">
                                      {typeof err.additionalContext === 'string'
                                        ? err.additionalContext
                                        : JSON.stringify(
                                            err.additionalContext,
                                            null,
                                            2
                                          )}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                ← Previous
              </button>

              <div className="page-info">
                Page {page} of {totalPages} ({totalCount} total errors)
              </div>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="page-size-select"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
